import React from "react";
import { useForm, useFormState } from "react-hook-form";

import { DateTime } from "luxon";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Edit from "@mui/icons-material/Edit";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import arrayIncludesAny from "../../shared/arrayIncludesAny.js";
import axios from "../axiosClient.js";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import useAlertSnackbar from "../hooks/useAlertSnackbar.jsx";
import Alert from "./common/Alert.jsx";
import CancelButton from "./common/CancelButton.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import FormDatePicker from "./common/FormDatePicker.jsx";
import FormStringInput from "./common/FormStringInput.jsx";
import ConfirmAction from "./ConfirmAction.jsx";

function EngineeringExpenseForm({
  // props
  project,
  expense,
  mutate,
}) {
  const [open, setOpen] = React.useState(false);

  const [error, setError] = React.useState(null);
  // This component doesn't care about the current state of the success message
  const [, setSuccess] = useAlertSnackbar("success", 30_000);
  const [loading, setLoading] = React.useState(false);

  const formTitle = React.useMemo(() => (expense?.id ? "Update Expense" : "Add Expense"), [expense?.id]);

  const today = React.useMemo(() => DateTime.now(), []);
  const defaultValues = React.useMemo(
    () => ({
      date: expense?.date ? DateTime.fromISO(expense.date) : today,
      vendor: expense?.vendor || "",
      description: expense?.description || "",
      amount: expense?.amount || "",
    }),
    [expense, today]
  );

  const { handleSubmit, control, reset } = useForm({ defaultValues, shouldUnregister: true });
  const { isDirty } = useFormState({ control });

  const handleOpen = React.useCallback(
    (event) => {
      event.stopPropagation();
      setOpen(true);
      reset(defaultValues);
    },
    [defaultValues, reset]
  );

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const onSubmit = React.useCallback(
    async (data) => {
      setLoading(true);

      const payload = {
        ...data,
        engineeringProjectId: project.id,
        date: data.date.toISODate(),
        amount: Number(data.amount),
      };

      try {
        if (expense) {
          await axios({
            method: "PATCH",
            url: `/api/engineering-expenses/${expense.id}`,
            data: payload,
          });
        } else {
          await axios({
            method: "POST",
            url: `/api/engineering-expenses`,
            data: payload,
          });
        }

        handleClose();
        if (mutate) {
          mutate();
        }
        setSuccess(`Successfully ${expense ? "updated" : "added"} expense.`);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
      setLoading(false);
    },
    [project.id, expense, handleClose, mutate, setSuccess]
  );

  const { roles } = useAuth0Roles();
  const canDelete = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );
  const handleDeleteConfirmation = React.useCallback(async () => {
    if (expense) {
      await axios({
        method: "DELETE",
        url: `/api/engineering-expenses/${expense.id}`,
      });
      handleClose();
      if (mutate) {
        mutate();
      }
    }
  }, [expense, handleClose, mutate]);

  return (
    <>
      {expense ? (
        <Tooltip title="Edit this expense" arrow>
          <IconButton
            aria-label="edit-expense"
            color="primary"
            onClick={handleOpen}
            data-cy={`edit-expense-${expense.id}`}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Add an expense" arrow>
          <IconButton aria-label="add-expense" color="secondary" onClick={handleOpen} data-cy="add-expense">
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        data-cy="expense-form"
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit(onSubmit),
            noValidate: true,
          },
        }}
      >
        <Alert message={error} setMessage={setError} level="error" data-cy="dialog-error" />
        <DialogTitleBar title={formTitle} Icon={AttachMoneyIcon} iconColor="secondary" />
        <DialogContent>
          <Grid container spacing={4} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormDatePicker
                control={control}
                name="date"
                label="Date"
                required
                rules={{ required: "Please enter the date of the expense" }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormStringInput control={control} name="vendor" label="Vendor" />
            </Grid>
            <Grid size={12}>
              <FormStringInput
                control={control}
                name="description"
                label="Description"
                required
                rules={{ required: "Please provide a description for this expense" }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormStringInput
                control={control}
                name="amount"
                label="Amount"
                type="number"
                units="$"
                required
                rules={{
                  required: "Please enter the amount for this expense",
                  min: { value: 0, message: "Amount cannot be negative" },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2, mr: "auto" }}>
            {!!expense && canDelete && (
              <ConfirmAction
                actionName="Delete"
                objectName="Expense"
                handleConfirmation={handleDeleteConfirmation}
                confirmKeyword="DELETE"
              />
            )}
          </Box>
          <Box sx={{ m: 2 }}>
            <CancelButton color="secondary" isDirty={isDirty} onClick={handleClose}>
              Cancel
            </CancelButton>
          </Box>
          <Box sx={{ m: 2 }}>
            <Button
              data-cy="submit-expense"
              disabled={loading}
              variant="contained"
              color="secondary"
              loading={loading}
              type="submit"
            >
              {expense ? "Update" : "Add"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EngineeringExpenseForm;
